import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { ChevronLeft, LogOut, User, CreditCard, Bell, Settings, Menu, X, Plus } from "lucide-react";
import NotificationBell from "../components/NotificationBell";
import { useAuth } from "../auth/AuthContext";
import { auth } from "../services/auth";
import { can, getUserType, USER_TYPES } from "../config/roles";
import { NAV_CONFIG } from "../config/navigation";
import { usePublicBrand } from "../context/BrandContext";

import { TiHome } from "react-icons/ti";
import { FaPalette, FaDollarSign, FaCrown, FaFolder, FaUsers, FaFilePdf } from "react-icons/fa6";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import { MdEventNote } from "react-icons/md";

/* ─── Style injection ─────────────────────────────────────────── */
const STYLE_ID = "user-layout-styles";
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

    .ul-shell { min-height:100vh; background:#f4f5f7; font-family:'DM Sans',sans-serif; }

    /* Sidebar */
    .ul-sidebar {
      position:fixed; left:0; top:0; height:100%; z-index:40;
      background:#000000;
      display:flex; flex-direction:column;
      transition:width 280ms cubic-bezier(.4,0,.2,1), transform 280ms cubic-bezier(.4,0,.2,1);
      overflow:hidden;
    }
    .ul-sidebar.expanded  { width:240px; }
    .ul-sidebar.collapsed { width:64px; }
    @media (max-width:767px) {
      .ul-sidebar { transform:translateX(-100%); width:240px !important; }
      .ul-sidebar.mobile-open { transform:translateX(0); }
    }

    /* Brand */
    .ul-brand {
      height:64px; display:flex; align-items:center; gap:12px;
      padding:0 14px; border-bottom:1px solid rgba(255,255,255,.1); flex-shrink:0;
    }
    .ul-brand-logo {
      width:36px; height:36px; border-radius:8px;
      background:#ffffff; flex-shrink:0;
      display:flex; align-items:center; justify-content:center; overflow:hidden;
    }
    .ul-brand-logo img { width:100%; height:100%; object-fit:contain; border-radius:8px; }
    .ul-brand-logo-text {
      font-family:'DM Sans',sans-serif;
      font-size:15px; color:#000000; letter-spacing:.04em;
    }
    .ul-brand-text { overflow:hidden; white-space:nowrap; flex:1; min-width:0; }
    .ul-brand-name {
      font-family:'DM Sans',sans-serif;
      font-size:17px; letter-spacing:.06em; color:#fff; line-height:1.1;
      overflow:hidden; text-overflow:ellipsis;
    }
    .ul-brand-tagline {
      font-family:'DM Sans',sans-serif;
      font-size:9px; letter-spacing:.04em;
      color:rgba(255,255,255,.3); margin-top:1px;
    }

    /* Role chip — desktop sidebar */
    .ul-role-chip {
      margin:10px 10px 0;
      padding:8px 10px; border-radius:7px;
      display:flex; align-items:center; gap:8px;
      overflow:hidden; flex-shrink:0; border:1px solid transparent;
    }
    .ul-role-dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; }
    .ul-role-info { overflow:hidden; }
    .ul-role-label {
      font-family:'DM Sans',sans-serif;
      font-size:10px; letter-spacing:.04em;
      white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
    }
    .ul-role-org {
      font-size:11px; white-space:nowrap; overflow:hidden;
      text-overflow:ellipsis; margin-top:1px; opacity:.65;
    }

    /* Role colour variants — all unified to white/gray on black sidebar */
    .ul-role-owner, .ul-role-individual, .ul-role-staff, .ul-role-invited {
      background:rgba(255,255,255,.07); border-color:rgba(255,255,255,.12);
    }
    .ul-role-owner .ul-role-dot, .ul-role-individual .ul-role-dot,
    .ul-role-staff .ul-role-dot, .ul-role-invited .ul-role-dot { background:#ffffff; }
    .ul-role-owner .ul-role-label, .ul-role-individual .ul-role-label,
    .ul-role-staff .ul-role-label, .ul-role-invited .ul-role-label { color:#ffffff; }
    .ul-role-owner .ul-role-org, .ul-role-staff .ul-role-org,
    .ul-role-invited .ul-role-org { color:rgba(255,255,255,.55); }

    /* Nav */
    .ul-nav-section {
      padding:14px 14px 4px;
      font-family:'DM Sans',sans-serif;
      font-size:9px; letter-spacing:.04em;
      color:rgba(255,255,255,.2); white-space:nowrap; overflow:hidden; flex-shrink:0;
    }
    .ul-nav { flex:1; overflow-y:auto; overflow-x:hidden; padding:4px 10px 16px; }
    .ul-nav::-webkit-scrollbar { width:3px; }
    .ul-nav::-webkit-scrollbar-thumb { background:rgba(255,255,255,.1); border-radius:2px; }

    .ul-nav-link {
      display:flex; align-items:center; gap:11px;
      padding:9px 10px; border-radius:7px; margin-bottom:2px;
      color:rgba(255,255,255,.5);
      font-size:13.5px; font-weight:500;
      text-decoration:none; white-space:nowrap; overflow:hidden;
      transition:background 180ms, color 180ms; position:relative;
    }
    .ul-nav-link:hover { background:rgba(255,255,255,.07); color:rgba(255,255,255,.9); }
    .ul-nav-link.active { background:rgba(255,255,255,.12); color:#ffffff; }
    .ul-nav-link.active::before {
      content:''; position:absolute; left:0; top:20%; bottom:20%;
      width:3px; border-radius:0 3px 3px 0; background:#ffffff;
    }
    .ul-nav-icon { flex-shrink:0; width:18px; display:flex; align-items:center; justify-content:center; }

    /* Collapse btn */
    .ul-collapse-btn {
      margin:10px; padding:8px; border-radius:7px;
      background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.07);
      color:rgba(255,255,255,.4); cursor:pointer;
      display:flex; align-items:center; justify-content:center;
      transition:background 180ms, color 180ms; flex-shrink:0;
    }
    .ul-collapse-btn:hover { background:rgba(255,255,255,.1); color:rgba(255,255,255,.8); }

    /* Main area */
    .ul-main { transition:margin-left 280ms cubic-bezier(.4,0,.2,1); min-height:100vh; }
    .ul-main.expanded  { margin-left:240px; }
    .ul-main.collapsed { margin-left:64px; }
    @media (max-width:767px) {
      .ul-main.expanded, .ul-main.collapsed { margin-left:0 !important; }
    }

    /* Topbar */
    .ul-topbar {
      position:fixed; top:0; right:0; height:64px; z-index:30;
      background:rgba(255,255,255,.95); backdrop-filter:blur(12px);
      border-bottom:1px solid #e8ecf0;
      display:flex; align-items:center; padding:0 20px; gap:12px;
      transition:left 280ms cubic-bezier(.4,0,.2,1);
    }
    .ul-topbar.expanded  { left:240px; }
    .ul-topbar.collapsed { left:64px; }
    @media (max-width:767px) { .ul-topbar { left:0 !important; } }

    /* Mobile role bar — shown only on mobile, fixed below topbar */
    .ul-mobile-role-bar {
      display:none;
      position:fixed; top:64px; left:0; right:0; z-index:29;
      height:30px; align-items:center; justify-content:center; gap:8px;
      font-family:'DM Sans',sans-serif;
      font-size:10px; letter-spacing:.04em;
      border-bottom:1px solid rgba(0,0,0,.06);
    }
    @media (max-width:767px) { .ul-mobile-role-bar { display:flex; } }
    .ul-mobile-role-bar .ul-role-dot { width:6px; height:6px; border-radius:50%; }

    .ul-mrb-owner, .ul-mrb-individual, .ul-mrb-staff, .ul-mrb-invited {
      background:rgba(0,0,0,.05); color:#374151;
    }
    .ul-mrb-owner .ul-role-dot, .ul-mrb-individual .ul-role-dot,
    .ul-mrb-staff .ul-role-dot, .ul-mrb-invited .ul-role-dot { background:#111111; }

    /* New Project button */
    .ul-new-btn {
      display:inline-flex; align-items:center; gap:7px;
      padding:8px 16px; border-radius:7px;
      background:#111111; color:#ffffff;
      font-size:13px; font-weight:600; border:none; cursor:pointer;
      transition:background 150ms; white-space:nowrap;
    }
    .ul-new-btn:hover { background:#333333; }
    @media (max-width:479px) { .ul-new-btn span { display:none; } }

    /* Company logo in topbar center */
    .ul-topbar-brand {
      position:absolute; left:50%; transform:translateX(-50%);
      display:flex; align-items:center; gap:8px; pointer-events:none;
    }
    .ul-topbar-brand img { height:36px; object-fit:contain; }
    .ul-topbar-brand-name {
      font-size:14px; font-weight:600; color:#0c1118; white-space:nowrap;
    }
    @media (max-width:767px) { .ul-topbar-brand { display:none; } }

    /* Profile button */
    .ul-profile-btn {
      display:flex; align-items:center; gap:9px;
      padding:6px 10px 6px 8px;
      border:1px solid #e8ecf0; border-radius:8px;
      background:#fff; cursor:pointer;
      transition:box-shadow 180ms, border-color 180ms;
    }
    .ul-profile-btn:hover { box-shadow:0 2px 8px rgba(0,0,0,.08); border-color:#d1d5db; }
    .ul-profile-avatar {
      width:32px; height:32px; border-radius:50%;
      object-fit:cover; border:2px solid #111111;
    }
    .ul-profile-name { font-size:13px; font-weight:600; color:#0c1118; }
    .ul-profile-sub  { font-family:'DM Sans',sans-serif; font-size:10px; color:#9ca3af; letter-spacing:.04em; }

    /* Dropdown */
    .ul-dropdown {
      position:absolute; right:0; top:calc(100% + 8px);
      width:240px; background:#fff;
      border:1px solid #e8ecf0; border-radius:10px;
      box-shadow:0 8px 28px rgba(0,0,0,.12);
      overflow:hidden; z-index:50;
    }
    .ul-dropdown-head {
      padding:14px 16px; background:#000000;
      display:flex; align-items:center; gap:10px;
    }
    .ul-dropdown-avatar { width:40px; height:40px; border-radius:50%; object-fit:cover; border:2px solid #ffffff; }
    .ul-dropdown-name  { font-size:14px; font-weight:600; color:#fff; }
    .ul-dropdown-email { font-size:11px; color:rgba(255,255,255,.5); margin-top:1px; }
    .ul-dropdown-item {
      display:flex; align-items:center; gap:10px;
      padding:10px 16px; font-size:13.5px; color:#374151;
      text-decoration:none; cursor:pointer;
      transition:background 150ms; background:none; border:none; width:100%; text-align:left;
    }
    .ul-dropdown-item:hover { background:#f9fafb; }
    .ul-dropdown-item.danger { color:#dc2626; }
    .ul-dropdown-item.danger:hover { background:#fef2f2; }
    .ul-dropdown-divider { height:1px; background:#f3f4f6; }

    /* Page */
    .ul-page { padding-top:64px; min-height:100vh; }
    .ul-page.has-mobile-bar { padding-top:94px; }
    .ul-page-inner { padding:24px; }
    @media (max-width:767px) { .ul-page-inner { padding:16px; } }

    /* Overlay */
    .ul-overlay {
      display:none; position:fixed; inset:0;
      background:rgba(0,0,0,.55); z-index:35; backdrop-filter:blur(2px);
    }
    @media (max-width:767px) { .ul-overlay.visible { display:block; } }

    /* Hamburger */
    .ul-hamburger {
      display:none; background:none; border:none;
      cursor:pointer; padding:4px; color:#374151;
    }
    @media (max-width:767px) { .ul-hamburger { display:flex; align-items:center; } }

    /* Hide topbar title on mobile */
    @media (max-width:767px) { .ul-topbar-title { display:none; } }

    /* Hide profile names and chevron on mobile */
    @media (max-width:767px) {
      .ul-profile-names { display:none; }
      .ul-profile-chevron { display:none; }
    }

    /* Sidebar sign-out button */
    .ul-signout-btn {
      margin: 0 10px 10px; padding: 9px 10px; border-radius: 7px;
      background: none; border: none; cursor: pointer;
      display: flex; align-items: center; gap: 10px;
      color: rgba(255,255,255,.4); font-size: 13.5px; font-weight: 500;
      font-family: 'DM Sans', sans-serif;
      transition: background 180ms, color 180ms; flex-shrink: 0;
      text-align: left; width: calc(100% - 20px);
    }
    .ul-signout-btn:hover { background: rgba(239,68,68,.15); color: #f87171; }
    .ul-signout-btn .ul-signout-icon { flex-shrink: 0; width: 18px; display: flex; align-items: center; justify-content: center; }
  `;
  document.head.appendChild(el);
}

/* ─── Role config ─────────────────────────────────────────────── */
const ROLE_META = {
  owner:      { label: "Company Owner",  chipClass: "ul-role-owner",      mrbClass: "ul-mrb-owner",      shortLabel: "OWNER"      },
  staff:      { label: "Staff",          chipClass: "ul-role-staff",      mrbClass: "ul-mrb-staff",      shortLabel: "STAFF"      },
  individual: { label: "Individual",     chipClass: "ul-role-individual",  mrbClass: "ul-mrb-individual", shortLabel: "INDIVIDUAL" },
  invited:    { label: "Member",         chipClass: "ul-role-invited",    mrbClass: "ul-mrb-invited",    shortLabel: "MEMBER"     },
};

function getRoleMeta(user) {
  if (!user) return ROLE_META.individual;
  const t = getUserType(user);
  if (t === USER_TYPES.OWNER)    return ROLE_META.owner;
  if (t === USER_TYPES.STAFF)    return ROLE_META.staff;
  if (t === USER_TYPES.INVITED)  return ROLE_META.invited;
  return ROLE_META.individual;
}

/* ─── Nav icon map ────────────────────────────────────────────── */
function iconFor(path) {
  if (path === "/dashboard")                return <TiHome size={18} />;
  if (path.includes("/branding"))           return <FaPalette size={16} />;
  if (path.includes("/autoestimator"))      return <FaDollarSign size={16} />;
  if (path.includes("/estimator"))          return <FaDollarSign size={16} />;
  if (path.includes("/subscription"))       return <FaCrown size={16} />;
  if (path.includes("/support"))            return <BsFillQuestionCircleFill size={16} />;
  if (path.includes("/project"))            return <FaFolder size={16} />;
  if (path.includes("/staff"))              return <FaUsers size={16} />;
  if (path.includes("/quotes"))             return <MdEventNote size={17} />;
  if (path.includes("/myquotes"))           return <MdEventNote size={17} />;
  if (path.includes("/engineering-plan"))   return <FaFilePdf size={16} />;
  return <TiHome size={18} />;
}

/* ─── Main layout ─────────────────────────────────────────────── */
export default function UserLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const publicBrand = usePublicBrand();
  const roleMeta = getRoleMeta(user);

  useEffect(() => { injectStyles(); }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const logoSrc     = publicBrand?.logoUrl     || null;
  const companyName = publicBrand?.companyName || "MyDesignerPlus";
  const isCompany   = user?.accountType === "organization";
  const orgName     = user?.orgName || null;

  return (
    <div className="ul-shell">
      {/* Mobile overlay */}
      <div className={`ul-overlay ${mobileOpen ? "visible" : ""}`} onClick={() => setMobileOpen(false)} />

      {/* Sidebar */}
      <aside className={`ul-sidebar ${collapsed ? "collapsed" : "expanded"} ${mobileOpen ? "mobile-open" : ""}`}>
        {/* Brand */}
        <div className="ul-brand">
          <div className="ul-brand-logo">
            {logoSrc
              ? <img src={logoSrc} alt={companyName} onError={(e) => { e.currentTarget.style.display = "none"; }} />
              : <span className="ul-brand-logo-text">MD</span>}
          </div>
          {!collapsed && (
            <div className="ul-brand-text">
              <div className="ul-brand-name">{companyName}</div>
              <div className="ul-brand-tagline">MyDesignerPlus</div>
            </div>
          )}
        </div>

        {/* Role chip */}
        {!collapsed && (
          <div className={`ul-role-chip ${roleMeta.chipClass}`}>
            <div className="ul-role-dot" />
            <div className="ul-role-info">
              <div className="ul-role-label">{roleMeta.shortLabel}</div>
              {orgName && <div className="ul-role-org">{orgName}</div>}
            </div>
          </div>
        )}

        {/* Nav section label */}
        {!collapsed && <div className="ul-nav-section">Navigation</div>}

        {/* Nav links */}
        <UserNav collapsed={collapsed} user={user} />

        {/* Sign Out */}
        <UserSidebarSignOut collapsed={collapsed} />

        {/* Collapse toggle */}
        <button
          className="ul-collapse-btn"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeft size={16} style={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 280ms" }} />
        </button>
      </aside>

      {/* Main */}
      <div className={`ul-main ${collapsed ? "collapsed" : "expanded"}`}>
        {/* Topbar */}
        <header className={`ul-topbar ${collapsed ? "collapsed" : "expanded"}`}>
          <button className="ul-hamburger" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <NewProjectButton />

          {/* Company branding in topbar center (desktop only, org accounts) */}
          {isCompany && (
            <div className="ul-topbar-brand">
              {logoSrc && (
                <img src={logoSrc} alt={companyName} onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}
              <span className="ul-topbar-brand-name">{companyName}</span>
            </div>
          )}

          <div style={{ flex: 1 }} />
          <NotificationBell />
          <ProfileMenu roleMeta={roleMeta} />
        </header>

        {/* Mobile role bar — visible only on mobile */}
        <div className={`ul-mobile-role-bar ${roleMeta.mrbClass}`}>
          <div className="ul-role-dot" />
          <span>{roleMeta.label}{orgName ? ` · ${orgName}` : ""}</span>
        </div>

        {/* Page content */}
        <div className="ul-page has-mobile-bar">
          <div className="ul-page-inner">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Sidebar sign-out ────────────────────────────────────────── */
function UserSidebarSignOut({ collapsed }) {
  async function handleSignOut() {
    try { await auth.logout(); } catch {}
    window.location.replace("/login");
  }
  return (
    <button className="ul-signout-btn" onClick={handleSignOut} title="Sign Out">
      <span className="ul-signout-icon"><LogOut size={16} /></span>
      {!collapsed && "Sign Out"}
    </button>
  );
}

/* ─── Nav ─────────────────────────────────────────────────────── */
function UserNav({ collapsed, user }) {
  const type  = getUserType(user);
  const items = (NAV_CONFIG[type] || []).filter((i) => can(user, i.perm));
  return (
    <nav className="ul-nav">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/dashboard"}
          className={({ isActive }) => `ul-nav-link${isActive ? " active" : ""}`}
          title={collapsed ? item.label : undefined}
        >
          <span className="ul-nav-icon">{iconFor(item.to)}</span>
          {!collapsed && <span>{item.label}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

/* ─── New project button ──────────────────────────────────────── */
function NewProjectButton() {
  const navigate = useNavigate();
  return (
    <button className="ul-new-btn" onClick={() => navigate("/dashboard/projects?intent=new")}>
      <Plus size={15} />
      <span>New Project</span>
    </button>
  );
}

/* ─── Profile menu ────────────────────────────────────────────── */
function ProfileMenu({ roleMeta }) {
  const [open, setOpen]   = useState(false);
  const [me,   setMe]     = useState(null);
  const ref               = useRef(null);

  useEffect(() => {
    function outside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) { document.addEventListener("mousedown", outside); return () => document.removeEventListener("mousedown", outside); }
  }, [open]);

  useEffect(() => {
    function esc(e) { if (e.key === "Escape") setOpen(false); }
    if (open) { document.addEventListener("keydown", esc); return () => document.removeEventListener("keydown", esc); }
  }, [open]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: "include", cache: "no-store" });
        if (res.ok) { const d = await res.json(); if (!ignore) setMe(d.safeUser); }
      } catch {}
    })();
    return () => { ignore = true; };
  }, []);

  const displayName = me?.firstname || me?.lastname
    ? `${me?.firstname || ""} ${me?.lastname || ""}`.trim()
    : me?.email || "User";
  const avatarSrc = me?.avatarUrl || "/assets/profile.png";

  async function handleSignOut() {
    setOpen(false);
    try { await auth.logout(); } catch {}
    window.location.replace("/login");
  }

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button className="ul-profile-btn" onClick={() => setOpen(!open)}>
        <img src={avatarSrc} alt="Profile" className="ul-profile-avatar" />
        <div className="ul-profile-names">
          <div className="ul-profile-name">{displayName}</div>
          <div className="ul-profile-sub">{roleMeta.shortLabel}</div>
        </div>
        <ChevronLeft size={14} className="ul-profile-chevron" style={{ color: "#9ca3af", transform: open ? "rotate(-90deg)" : "rotate(90deg)", transition: "transform 200ms" }} />
      </button>

      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 45 }} onClick={() => setOpen(false)} />
          <div className="ul-dropdown">
            <div className="ul-dropdown-head">
              <img src={avatarSrc} alt="" className="ul-dropdown-avatar" />
              <div>
                <div className="ul-dropdown-name">{displayName}</div>
                <div className="ul-dropdown-email">{me?.email}</div>
              </div>
            </div>
            <div style={{ padding: "6px 0" }}>
              <Link to="/dashboard/profile"       className="ul-dropdown-item" onClick={() => setOpen(false)}><User size={15} /> Profile Settings</Link>
              <Link to="/dashboard/subscription"  className="ul-dropdown-item" onClick={() => setOpen(false)}><CreditCard size={15} /> Subscription &amp; Billing</Link>
              <Link to="/dashboard/notifications" className="ul-dropdown-item" onClick={() => setOpen(false)}><Settings size={15} /> Notifications</Link>
            </div>
            <div className="ul-dropdown-divider" />
            <div style={{ padding: "6px 0" }}>
              <button onClick={handleSignOut} className="ul-dropdown-item danger"><LogOut size={15} /> Sign Out</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
